import React, { useState } from 'react';
import { X } from 'lucide-react';

interface CanvasNode {
  id: string;
  type: string;
  x: number;
  y: number;
  config?: any;
}

interface Connection {
  from: string;
  to: string;
}

interface ConnectionLinesProps {
  nodes: CanvasNode[];
  connections: Connection[];
  errorConnectionKeys: Set<string>;
  connectingFrom: string | null;
  connectingMouse: { x: number; y: number } | null;
  onRemoveConnection: (from: string, to: string) => void;
  isActive?: boolean;
}

const NODE_WIDTH = 220;
const NODE_HEIGHT_HALF = 45;

/*
 * The wires between workflow nodes.
 *
 * Everything here lives in the canvas's own LTR pixel space — node x/y,
 * bezier control points, the delete button's placement — so the coordinates
 * stay physical (`left`/`top`) and inline. SVG strokes cannot read Tailwind
 * classes, so the two colours come from CSS variables directly.
 */
export const ConnectionLines: React.FC<ConnectionLinesProps> = ({
  nodes, connections, errorConnectionKeys, connectingFrom, connectingMouse, onRemoveConnection, isActive = false
}) => {
  const [hoveredConn, setHoveredConn] = useState<string | null>(null);

  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  const ink = 'var(--foreground)';
  const live = 'var(--success)';
  const error = 'var(--destructive)';

  const renderConnection = (conn: Connection, index: number) => {
    const from = nodeMap.get(conn.from);
    const to = nodeMap.get(conn.to);
    if (!from || !to) return null;

    const fromX = from.x + NODE_WIDTH;
    const fromY = from.y + NODE_HEIGHT_HALF;
    const toX = to.x;
    const toY = to.y + NODE_HEIGHT_HALF;
    const midX = (fromX + toX) / 2;

    const path = `M ${fromX} ${fromY} C ${midX} ${fromY}, ${midX} ${toY}, ${toX} ${toY}`;
    const key = `${conn.from}-${conn.to}`;
    const isError = errorConnectionKeys.has(key);
    const isHovered = hoveredConn === key;
    const color = isError ? error : ink;

    return (
      <React.Fragment key={key}>
        <g
          onMouseEnter={() => setHoveredConn(key)}
          onMouseLeave={() => setHoveredConn(null)}
        >
          {/* Invisible thick line for hover detection */}
          <path
            d={path}
            fill="none"
            stroke="transparent"
            strokeWidth="20"
            style={{ cursor: 'pointer' }}
          />
          {/* Visible line */}
          <path
            d={path}
            fill="none"
            stroke={isActive && !isError ? live : color}
            strokeWidth={isHovered ? 2.5 : 2}
            strokeDasharray="6,4"
            opacity={isError ? 0.6 : isActive ? 0.5 : isHovered ? 0.4 : 0.25}
            style={{
              ...(isActive && !isError ? { animation: `flowAnim 3s linear infinite`, animationDelay: `${index * 0.4}s` } : {}),
              pointerEvents: 'none',
              transition: 'opacity 0.2s ease, stroke-width 0.15s ease, stroke 0.2s ease',
            }}
          />
          {/* Arrow dot at target */}
          <circle
            cx={toX}
            cy={toY}
            r="4"
            fill={isActive && !isError ? live : color}
            opacity={isError ? 0.7 : isActive ? 0.6 : 0.3}
            style={{ pointerEvents: 'none' }}
          />
        </g>
      </React.Fragment>
    );
  };

  // Dragging preview line
  const renderDraggingLine = () => {
    if (!connectingFrom || !connectingMouse) return null;
    const from = nodeMap.get(connectingFrom);
    if (!from) return null;

    const fromX = from.x + NODE_WIDTH;
    const fromY = from.y + NODE_HEIGHT_HALF;
    const toX = connectingMouse.x;
    const toY = connectingMouse.y;
    const midX = (fromX + toX) / 2;

    const path = `M ${fromX} ${fromY} C ${midX} ${fromY}, ${midX} ${toY}, ${toX} ${toY}`;

    return (
      <path
        d={path}
        fill="none"
        stroke={ink}
        strokeWidth="2"
        strokeDasharray="4,4"
        opacity={0.5}
        style={{ pointerEvents: 'none' }}
      />
    );
  };

  // Delete buttons rendered as HTML overlays (outside SVG)
  const renderDeleteButtons = () => {
    return connections.map((conn) => {
      const from = nodeMap.get(conn.from);
      const to = nodeMap.get(conn.to);
      if (!from || !to) return null;

      const key = `${conn.from}-${conn.to}`;
      if (hoveredConn !== key) return null;

      const fromX = from.x + NODE_WIDTH;
      const fromY = from.y + NODE_HEIGHT_HALF;
      const toX = to.x;
      const toY = to.y + NODE_HEIGHT_HALF;
      const btnX = (fromX + toX) / 2;
      const btnY = (fromY + toY) / 2;

      return (
        <button
          key={`del-${key}`}
          type="button"
          onClick={() => onRemoveConnection(conn.from, conn.to)}
          onMouseEnter={() => setHoveredConn(key)}
          onMouseLeave={() => setHoveredConn(null)}
          aria-label="Remove connection"
          className="absolute z-[5] flex size-5 animate-in items-center justify-center rounded-full bg-destructive text-white zoom-in-50 hover:brightness-90"
          style={{ left: btnX - 10, top: btnY - 10 }}
        >
          <X className="size-3" />
        </button>
      );
    });
  };

  return (
    <>
      <svg className="pointer-events-none absolute top-0 left-0 z-0 h-full w-full [&_g]:pointer-events-auto">
        <style>{`@keyframes flowAnim { 0% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: -100; } }`}</style>
        {connections.map((conn, i) => renderConnection(conn, i))}
        {renderDraggingLine()}
      </svg>
      {/* Delete buttons as HTML overlays */}
      {renderDeleteButtons()}
    </>
  );
};
